import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowUpRight, ChevronRight } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/motion";
import { SectionLabel, BtnPrimary } from "../components/common";
import { PRODUCTS } from "../data/site";
import SteamProduct from "./SteamProduct";

const PLASMA_GREEN = "#6db500"; // same accent as the Steam Sterilizer master table

const Block = ({ id, label, title, children }) => (
  <section id={id} className="py-14 md:py-20 border-t border-slate-200 scroll-mt-24">
    <div className="grid lg:grid-cols-12 gap-8 lg:gap-16">
      <div className="lg:col-span-4">
        <SectionLabel>{label}</SectionLabel>
        {title && <h2 className="mt-5 font-heading text-2xl md:text-3xl font-bold tracking-tight text-brand-950">{title}</h2>}
      </div>
      <div className="lg:col-span-8 min-w-0">{children}</div>
    </div>
  </section>
);

export default function ProductDetail() {
  const { slug } = useParams();
  const product = PRODUCTS.find((p) => p.slug === slug);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!product) return <Navigate to="/products" replace />;

  if (product.line === "Steam Sterilizers") return <SteamProduct product={product} />;

  const related = (product.related || []).map((s) => PRODUCTS.find((p) => p.slug === s)).filter(Boolean);

  return (
    <>
      <Seo
        title={`${product.name} | STERILTRON`}
        description={product.intro}
        path={`/products/${product.slug}`}
      />

      {/* HERO */}
      <section className="relative pt-28 md:pt-36 bg-brand-950 text-white overflow-hidden">
        <div className="absolute inset-0 bp-grid-dark opacity-50" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-10 pb-0">
          <nav className="flex items-center gap-2 text-xs text-brand-300 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link><ChevronRight size={12} />
            <Link to="/products" className="hover:text-white">Products</Link><ChevronRight size={12} />
            <span className="text-brand-300">{product.category}</span>
            {product.doorType && (<><ChevronRight size={12} /><span className="text-brand-100">{product.doorType}</span></>)}
          </nav>
          <div className="grid lg:grid-cols-2 gap-12 items-end pb-0">
            <div className="pb-14">
              <SectionLabel dark>{product.category}</SectionLabel>
              <h1 className="mt-6 font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[0.95]">{product.name}</h1>
              <p className="mt-6 text-brand-100 text-base md:text-lg leading-relaxed max-w-lg">{product.tagline}</p>
              <div className="mt-8"><BtnPrimary to="/contact" testid="product-request-top" className="bg-white !text-brand-900 hover:bg-brand-100">Request Information</BtnPrimary></div>
            </div>
            <motion.div initial={{ opacity: 0, scale: 1.08 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} className="relative">
              <img src={product.image} alt={product.name} className={`w-full h-[340px] md:h-[460px] ${product.imageFit === "contain" ? "object-contain object-center bg-[#c6c6c9]" : "object-cover"}`} />
            </motion.div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="py-14 md:py-20">
          <p className="font-heading text-2xl md:text-3xl font-medium tracking-tight text-brand-950 max-w-4xl leading-snug text-balance">{product.intro}</p>
        </Reveal>

        <Block id="overview" label="Overview" title="System Overview">
          <p className="text-slate-700 text-base md:text-lg leading-relaxed">{product.overview}</p>
        </Block>

        <Block id="benefits" label="Key Benefits" title="Why it matters">
          <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-5">
            {product.benefits.map((b) => (
              <li key={b} className="flex items-start gap-3 text-slate-700">
                <span className="mt-1 flex-shrink-0 w-5 h-5 bg-brand-800 text-white flex items-center justify-center"><Check size={13} /></span>
                <span className="leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        </Block>

        <Block id="applications" label="Applications" title="Where it's used">
          <div className="grid sm:grid-cols-2 gap-px bg-slate-200 border border-slate-200">
            {product.applications.map((a) => (
              <div key={a} className="bg-white p-6 text-slate-700">{a}</div>
            ))}
          </div>
        </Block>

        <Block id="technology" label="Technology" title="How it works">
          <p className="text-slate-700 text-base md:text-lg leading-relaxed">{product.technology}</p>
          {product.process && (
            <ol className="mt-8 grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-slate-200 border border-slate-200" data-testid={`${product.slug}-process`}>
              {product.process.map((s, i) => (
                <li key={s} className="bg-white p-5">
                  <span className="font-mono text-xs" style={{ color: PLASMA_GREEN }}>{String(i + 1).padStart(2, "0")}</span>
                  <span className="mt-2 block text-sm font-semibold text-brand-950">{s}</span>
                </li>
              ))}
            </ol>
          )}
        </Block>

        <Block id="features" label="Technical Features">
          <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
            {product.techFeatures.map((f, i) => (
              <div key={f} className="flex items-center gap-4 py-3 border-b border-slate-200">
                <span className="font-mono text-xs text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-slate-700">{f}</span>
              </div>
            ))}
          </div>
        </Block>

        {!product.specTable && !product.specsPending && (
          <>
            <Block id="specifications" label="Specifications">
              <table className="w-full border border-slate-200">
                <tbody>
                  {product.specs.map(([k, v], i) => (
                    <tr key={k} className={i % 2 ? "bg-white" : "bg-brand-50/40"}>
                      <td className="py-4 px-6 font-semibold text-brand-950 w-1/2 border-b border-slate-200">{k}</td>
                      <td className="py-4 px-6 text-slate-700 border-b border-slate-200 font-mono text-sm">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Block>

            <Block id="options" label="Optional Configurations">
              <div className="flex flex-wrap gap-3">
                {product.options.map((o) => (
                  <span key={o} className="px-5 py-3 border border-slate-300 text-sm text-slate-700 hover:border-brand-800 hover:text-brand-800 transition-colors">{o}</span>
                ))}
              </div>
            </Block>

            <Block id="gallery" label="Gallery">
              {product.gallery ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid={`${product.slug}-gallery`}>
                  {product.gallery.map((src, i) => (
                    <div key={i} className="border border-slate-200 bg-slate-50 aspect-[4/3] flex items-center justify-center overflow-hidden">
                      <img src={src} alt={`${product.name} view ${i + 1}`} loading="lazy" className="w-full h-full object-contain object-center" />
                    </div>
                  ))}
                </div>
              ) : (
              <div className="grid grid-cols-2 gap-4">
                {[product.image, product.image].map((src, i) => (
                  <div key={i} className={`overflow-hidden border border-slate-200 ${product.imageFit === "contain" ? "bg-[#c6c6c9]" : "bg-slate-900"} ${i === 0 ? "col-span-2" : ""}`}>
                    <img src={src} alt={`${product.name} view ${i + 1}`} loading="lazy" className={`w-full ${product.imageFit === "contain" ? "object-contain object-center" : "object-cover"} ${i === 0 ? "h-72 md:h-96" : "h-48 md:h-64"}`} />
                  </div>
                ))}
              </div>
              )}
            </Block>
          </>
        )}
      </div>

      {(product.specTable || product.specsPending) && (
        <>
          {/* TECHNICAL SPECIFICATIONS — same layout system as the Steam Sterilizer master page */}
          <section id="specifications" className="py-12 md:py-16 border-t border-slate-200">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
              <div className="flex items-center gap-4">
                <span className="h-6 w-1" style={{ backgroundColor: PLASMA_GREEN }} />
                <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900 uppercase">Technical Specifications</h2>
              </div>

              <div className="mt-10 overflow-x-auto">
                {product.specTable ? (
                <table className="w-full border-collapse text-sm" style={{ minWidth: product.specTable.minWidth }} data-testid={`${product.slug}-spec-table`}>
                  <thead>
                    <tr className="text-slate-700">
                      <th rowSpan={2} style={{ minWidth: product.specTable.leadW }} className="border border-slate-200 bg-slate-50 px-4 py-3 text-left align-middle font-semibold">{product.specTable.lead}</th>
                      {product.specTable.groups.map((g) => (
                        <th key={g.label} colSpan={g.span} className="border border-slate-200 px-4 py-2.5 text-center font-semibold uppercase tracking-wide text-xs" style={{ color: PLASMA_GREEN }}>{g.label}</th>
                      ))}
                      <th rowSpan={2} style={{ minWidth: product.specTable.trailW }} className="border border-slate-200 bg-slate-50 px-4 py-3 text-center align-middle font-semibold">{product.specTable.trail}</th>
                    </tr>
                    <tr className="text-slate-500 text-xs">
                      {product.specTable.subheaders.map((h, i) => (
                        <th key={h} style={{ minWidth: product.specTable.widths[i] }} className="border border-slate-200 bg-slate-50 px-4 py-2 font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="text-slate-800 text-center">
                      <td className="border border-slate-200 px-4 py-4 text-left font-heading font-bold text-slate-900">{product.name}</td>
                      {product.specTable.values.map((val, i) => (
                        <td key={i} className="border border-slate-200 px-4 py-4">{val}</td>
                      ))}
                      <td className="border border-slate-200 px-4 py-4 text-left">{product.specTable.standards}</td>
                    </tr>
                  </tbody>
                </table>
                ) : (
                <p className="text-xs md:text-sm text-slate-400" data-testid={`${product.slug}-specs-pending`}>
                  Final technical specifications for {product.name} will be published once the approved specification document is available.
                </p>
                )}
              </div>

              {product.specNotes && (
                <div className="mt-8 space-y-2.5 text-xs md:text-sm text-slate-500 leading-relaxed max-w-3xl" data-testid={`${product.slug}-spec-notes`}>
                  {product.specNotes.map((n, i) => (
                    <p key={i}><span className="font-semibold mr-1" style={{ color: PLASMA_GREEN }}>■</span> {n}</p>
                  ))}
                </div>
              )}

              {/* OPTIONAL CONFIGURATIONS — secondary position, after the technical table */}
              {product.options.length > 0 && (
              <div className="mt-10" data-testid={`${product.slug}-options`}>
                <p className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: PLASMA_GREEN }}>Optional Configurations</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  {product.options.map((o) => (
                    <span key={o} className="px-5 py-3 border border-slate-300 text-sm text-slate-700 hover:border-brand-800 hover:text-brand-800 transition-colors">{o}</span>
                  ))}
                </div>
              </div>
              )}
            </div>
          </section>

          {/* GALLERY — same 3-card structure as the Steam Sterilizer master page */}
          <section id="gallery" className="pb-24 md:pb-32">
            <div className="max-w-6xl mx-auto px-6 md:px-12">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid={`${product.slug}-gallery`}>
                {product.gallery.map((src, i) => (
                  <div key={i} className="border border-slate-200 bg-slate-50 aspect-[4/3] flex items-center justify-center overflow-hidden">
                    <img src={src} alt={`${product.name} view ${i + 1}`} loading="lazy" className="w-full h-full object-contain object-center" />
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}

      {/* RELATED */}
      {related.length > 0 && (
        <section className="py-20 bg-brand-50/40 border-y border-slate-200 mt-10">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <SectionLabel>Related Products</SectionLabel>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p) => (
                <Link key={p.slug} to={`/products/${p.slug}`} data-testid={`related-${p.slug}`} className="group block bg-white border border-slate-200 hover:border-brand-800 transition-colors">
                  <div className="overflow-hidden bg-slate-900">
                    <img src={p.image} alt={p.name} loading="lazy" className="w-full h-52 object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-heading text-base font-semibold tracking-tight text-brand-950 leading-snug">{p.name}</h3>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-brand-800">View <ArrowUpRight size={13} /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* REQUEST INFO CTA */}
      <section className="bg-brand-900 text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter max-w-xl">Request information about the {product.name.replace("STERILTRON ", "")}</h2>
            <p className="mt-4 text-brand-100 max-w-lg">Our engineering team will help specify the right configuration for your facility.</p>
          </div>
          <Link to="/contact" data-testid="product-request-bottom" className="flex-shrink-0 group inline-flex items-center gap-2 bg-white text-brand-900 px-10 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-brand-100 transition-colors">
            Request Information <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </section>
    </>
  );
}
